// ---------------------------------------------------------------------------
// GraderEvaluateTab — pure helpers. React-free logic lifted out of the tab
// component: deterministic backing-resource naming, the convention default for
// a variable's runtime binding, and assembly of the backing template body
// (with: clauses + the GRADER_MODEL run input).
// ---------------------------------------------------------------------------

import {
  DEFAULT_GRADER_MODEL,
  GRADER_MODEL_VARIABLE,
  type GraderType,
  type JudgeGraderSpec,
} from '../../../../services/grader.interface.js';
import type { VariableDeclaration } from '../../../../types/agent.js';
import { AGENT_NAME_MAX_LENGTH } from '../../../../types/agent.js';
import type { UpsertEvaluationTemplateBody } from '../../../../types/evaluationTemplate.js';
import { EVALUATION_TEMPLATE_NAME_MAX_LENGTH } from '../../../../types/evaluationTemplate.js';
import {
  applyBindingsToParts,
  defaultBindingFor,
  type BindingRow,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindings.utils.js';
import { seedFormStateFromTemplate } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.seed.js';
import type {
  EvaluationTemplateWithConfig,
  SeededFormState,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import type { TemplateBodyParts } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.utils.js';

const RESOURCE_NAME_PREFIX = 'grader-eval-';
export const AGENT_RESPONSE_VARIABLE = 'AGENT_RESPONSE';
const TURN_OUTPUT_EXPRESSION = /\{\{\$expr:\s*\$\.turn\.output\s*\}\}/g;
const AGENT_RESPONSE_REFERENCE = `{{$var:${AGENT_RESPONSE_VARIABLE}}}`;

export type AdaptedGraderSubject =
  | {
      spec: unknown;
      variables?: Record<string, VariableDeclaration>;
      requiresClone: false;
    }
  | {
      spec: JudgeGraderSpec;
      variables: Record<string, VariableDeclaration>;
      requiresClone: true;
    };

export function adaptGraderSubjectForAgent(
  spec: unknown,
  variables?: Record<string, VariableDeclaration>,
): AdaptedGraderSubject {
  if (
    typeof spec !== 'object' ||
    spec === null ||
    !('graderType' in spec) ||
    spec.graderType !== ('LlmGraderSpec' satisfies GraderType) ||
    !('invoke' in spec) ||
    typeof spec.invoke !== 'object' ||
    spec.invoke === null ||
    !('userMessage' in spec.invoke) ||
    typeof spec.invoke.userMessage !== 'string' ||
    spec.invoke.userMessage.search(TURN_OUTPUT_EXPRESSION) === -1
  ) {
    return { spec, variables, requiresClone: false };
  }

  const llmSpec = spec as JudgeGraderSpec;
  return {
    spec: {
      ...llmSpec,
      invoke: {
        ...llmSpec.invoke,
        userMessage: spec.invoke.userMessage.replace(
          TURN_OUTPUT_EXPRESSION,
          AGENT_RESPONSE_REFERENCE,
        ),
      },
    },
    variables: {
      ...variables,
      [AGENT_RESPONSE_VARIABLE]: variables?.[AGENT_RESPONSE_VARIABLE] ?? {
        type: 'String',
      },
    },
    requiresClone: true,
  };
}

// Agent and template names share the same `^[a-z][a-z0-9-]*$` / length
// constraints, so a single deterministic name backs both the grader-as-agent
// adapter and the template per grader.
const RESOURCE_NAME_MAX_LENGTH = Math.min(
  AGENT_NAME_MAX_LENGTH,
  EVALUATION_TEMPLATE_NAME_MAX_LENGTH,
);

// Stable 32-bit hash (djb2) of the original grader name, base36-encoded so it
// fits the `[a-z0-9-]` name charset. Used to disambiguate names that would
// otherwise truncate to the same backing-resource name.
function hashSuffix(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  // >>> 0 → unsigned; base36 keeps it within [0-9a-z].
  return (hash >>> 0).toString(36);
}

// Deterministic, pattern-valid backing-resource name for a grader. Grader names
// already follow the same `^[a-z][a-z0-9-]*$` shape, but we sanitize + cap
// length defensively so a stray character never produces an invalid name.
// When the name is too long to fit, we append a hash of the *full* name rather
// than plain-truncating, so two distinct long names that share a prefix don't
// collapse to the same resource (which create-or-replace would silently
// overwrite, sharing a jobs rail).
export function backingResourceName(graderName: string): string {
  const sanitized =
    graderName
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+/, '') || 'grader';
  const available = RESOURCE_NAME_MAX_LENGTH - RESOURCE_NAME_PREFIX.length;
  if (sanitized.length <= available) {
    return `${RESOURCE_NAME_PREFIX}${sanitized}`;
  }
  const suffix = hashSuffix(graderName);
  const head = sanitized.slice(0, available - suffix.length - 1);
  return `${RESOURCE_NAME_PREFIX}${head}-${suffix}`;
}

// Convention default for a grader variable's runtime binding. The
// grader-under-test's output is exposed as `$.turn.output` (conventionally read
// into GRADER_SCORE / GRADER_OUTPUT); every other variable falls back to the
// shared query-row default. Both are editable in the Bindings card, so this is
// only a starting guess.
export function graderDefaultBindingFor(varName: string): string {
  if (varName === 'GRADER_SCORE' || varName === 'GRADER_OUTPUT') {
    return '{{$expr:$.turn.output}}';
  }
  return defaultBindingFor(varName);
}

export interface AssembleTemplateBodyParams {
  graderName: string;
  parts: TemplateBodyParts;
  graderModel: string;
  bindingRows: BindingRow[];
}

/**
 * Finish the backing template body from a `buildTemplateBody` result: declare
 * the GRADER_MODEL run input, then emit the with: bindings (shared assembly).
 */
export function assembleTemplateBody({
  graderName,
  parts,
  graderModel,
  bindingRows,
}: AssembleTemplateBodyParams): UpsertEvaluationTemplateBody {
  const variables = {
    ...parts.variables,
    [GRADER_MODEL_VARIABLE]: {
      type: 'string',
      default: graderModel.trim() || DEFAULT_GRADER_MODEL,
    },
  };

  const withBindings = applyBindingsToParts(
    { ...parts, variables },
    bindingRows,
  );

  return {
    displayName: `Evaluate grader ${graderName}`,
    ...withBindings,
  } as unknown as UpsertEvaluationTemplateBody;
}

export interface BackingTemplateSeed {
  seed: SeededFormState;
  graderModel: string;
}

/**
 * Map a previously-saved backing template into the tab's seed shape so
 * re-entering restores the last configuration instead of a blank form. Two
 * grader-specific artifacts are peeled back off, because the tab re-derives
 * them on every run and surfacing them as normal inputs would be confusing or
 * would double-declare them:
 *   • GRADER_MODEL — belongs in the model picker, not the variables list; its
 *     saved value is returned separately to seed that picker.
 *   • the subject grader row — injected into the task's graders[] at run time
 *     (it's implicit from the page), so it must not reappear as a chosen
 *     evaluating grader.
 */
export function seedFromBackingTemplate(
  template: EvaluationTemplateWithConfig,
  graderName: string,
  resourceName: string,
): BackingTemplateSeed {
  const base = seedFormStateFromTemplate(template);
  const savedModel = template.variables?.[GRADER_MODEL_VARIABLE]?.default;
  const graderModel =
    typeof savedModel === 'string' && savedModel.trim().length > 0
      ? savedModel
      : DEFAULT_GRADER_MODEL;

  const seed: SeededFormState = {
    ...base,
    variables: base.variables.filter(
      (variable) => variable.name !== GRADER_MODEL_VARIABLE,
    ),
    taskDrafts: base.taskDrafts.map((task, index) =>
      index === 0
        ? {
            ...task,
            graderRows: task.graderRows.filter(
              (row) => row.name !== graderName && row.name !== resourceName,
            ),
          }
        : task,
    ),
  };

  return { seed, graderModel };
}
